import { Component, OnInit, ViewChild, Inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatStepperModule, MatStepper } from '@angular/material/stepper';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatCardModule } from '@angular/material/card';
import { RegisterService, RegistrationDetails } from '../../services/register.service';
import { UtilService } from '../../services/util.service';
import { MeterDetails } from '../../models/registration.models';
import { ConsentWalletFcComponent } from '../consent-wallet-fc.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MeterSelectionComponent } from '../meter-selection/meter-selection.component';
import { CustomerDetailsComponent } from '../customer-details/customer-details.component';
import { CdkStepperModule } from '@angular/cdk/stepper';
import { LoginService } from '../../services/login.service';

export interface OnboardMeterDialogData {
  email: string;
  isTpi: boolean;
}

@Component({
  selector: 'app-onboard-meter-dialog',
  templateUrl: './onboard-meter-dialog.component.html',
  styleUrls: ['./onboard-meter-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    CdkStepperModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatCardModule,
    ReactiveFormsModule,
    FormsModule,
    ConsentWalletFcComponent,
    MeterSelectionComponent,
    CustomerDetailsComponent
  ]
})
export class OnboardMeterDialogComponent implements OnInit {
  @ViewChild('stepper') stepper!: MatStepper;

  onboardForm: FormGroup;
  registrationInProgress = false;

  constructor(
    private fb: FormBuilder,
    private regService: RegisterService,
    private loginService: LoginService,
    private util: UtilService,
    public dialogRef: MatDialogRef<OnboardMeterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: OnboardMeterDialogData
  ) {
    this.onboardForm = this.fb.group({
      // Step 1: Customer Details
      customerDetails: [null], // Remove Validators.required since validation is handled by the custom component
      
      // Step 2: Meter Selection
      meterSelection: [null, Validators.required],
      
      // Step 3: Consent
      consent: [false] // Remove Validators.requiredTrue since file uploads don't need consent
    });
  }

  ngOnInit() {
    // If not TPI, disable customer details entirely and set default values
    if (!this.data.isTpi) {
      const defaultCustomerDetails = {
        registeringAs: this.loginService.loginUser?.Organization ? 'company' : 'individual',
        personalDetails: {
          firstName: this.loginService.loginUser?.Firstname || 'N/A',
          lastName: this.loginService.loginUser?.Lastname || 'N/A',
          email: this.loginService.loginUser?.Email || 'N/A',
          phone: this.loginService.loginUser?.Phone || 'N/A'
        },
        companyDetails: {
          companyNumber: '',
          companyName: ''
        }
      };
      
      this.onboardForm.get('customerDetails')?.setValue(defaultCustomerDetails);
      this.onboardForm.get('customerDetails')?.disable();
    }

    // Watch for meter selection changes
    this.onboardForm.get('meterSelection')?.valueChanges.subscribe(val => {
      // Auto-set consent to true for file uploads since we don't need explicit consent
      if (val instanceof File) {
        this.onboardForm.get('consent')?.setValue(true);
      } else {
        // Reset consent for individual meter selections
        this.onboardForm.get('consent')?.setValue(false);
      }
    });
  }

  isStep1Valid(): boolean {
    const customerDetailsControl = this.onboardForm.get('customerDetails');
    if (!customerDetailsControl) return false;
    
    // Since we're using a custom component, we can check its validity directly
    return customerDetailsControl.valid;
  }

  isStep2Valid(): boolean {
    const meterSelection = this.onboardForm.get('meterSelection')?.value;
    return (Array.isArray(meterSelection) && meterSelection.length > 0) || 
           (meterSelection instanceof File);
  }

  isStep3Valid(): boolean {
    // For file uploads, consent is not required (emails will be sent)
    if (this.isFileUploadSelected()) {
      return true;
    }
    // For individual meters, consent is required
    return !!this.onboardForm.get('consent')?.value;
  }

  isFileUploadSelected(): boolean {
    const meterSelection = this.onboardForm.get('meterSelection')?.value;
    return meterSelection instanceof File;
  }

  getSelectedFileName(): string {
    const meterSelection = this.onboardForm.get('meterSelection')?.value;
    if (meterSelection instanceof File) {
      return meterSelection.name;
    }
    return '';
  }

  canFinish(): boolean {
    const meterSelection = this.onboardForm.get('meterSelection')?.value;
    const meterSelectionValid = (Array.isArray(meterSelection) && meterSelection.length > 0) || 
                               (meterSelection instanceof File);
    
    // For file uploads, consent is not required (emails will be sent automatically)
    let consentValid = true;
    if (!this.isFileUploadSelected()) {
      consentValid = !!this.onboardForm.get('consent')?.value;
    }
    
    if (this.data.isTpi) {
      // For TPI users, also check customer details
      const customerDetailsValid = this.isStep1Valid();
      return customerDetailsValid && meterSelectionValid && consentValid;
    } else {
      // For non-TPI users, only check meter selection and consent (if not file upload)
      return meterSelectionValid && consentValid;
    }
  }

  private isSmartMeter(meterDetails: MeterDetails): boolean {
    return meterDetails?.meterType?.toLowerCase().startsWith('s');
  }

  // Helper method to get meter selection type for debugging
  getMeterSelectionType(): string {
    const meterSelection = this.onboardForm.get('meterSelection')?.value;
    if (meterSelection instanceof File) {
      return `File: ${meterSelection.name}`;
    } else if (Array.isArray(meterSelection)) {
      return `Array with ${meterSelection.length} meters`;
    } else if (meterSelection === null) {
      return 'No selection';
    }
    return 'Unknown type';
  }

  // Step navigation helper methods
  getMaxStepIndex(): number {
    // If TPI, we have 3 steps (0, 1, 2), otherwise 2 steps (0, 1)
    return this.data.isTpi ? 2 : 1;
  }

  isLastStep(): boolean {
    return this.stepper?.selectedIndex === this.getMaxStepIndex();
  }

  isCurrentStepValid(): boolean {
    const currentIndex = this.stepper?.selectedIndex || 0;
    
    if (this.data.isTpi) {
      // For TPI users: Customer Details -> Meter Selection -> Consent
      if (currentIndex === 0) return this.isStep1Valid();
      if (currentIndex === 1) return this.isStep2Valid();
      if (currentIndex === 2) return this.isStep3Valid();
    } else {
      // For non-TPI users: Meter Selection -> Consent
      if (currentIndex === 0) return this.isStep2Valid();
      if (currentIndex === 1) return this.isStep3Valid();
    }
    
    return false;
  }

  getCustomerDetailsForConsent() {
    const customerDetails = this.onboardForm.get('customerDetails')?.value;
    if (!customerDetails) return undefined;
    
    const personalDetails = customerDetails.personalDetails;
    const isCompany = customerDetails.registeringAs === 'company';
    
    let details: any = {
      email: personalDetails?.email,
      firstName: personalDetails?.firstName,
      lastName: personalDetails?.lastName,
      phoneNumber: personalDetails?.phone,
      signUpType: isCompany ? 'company' : 'individual'
    };

    if (isCompany) {
      const companyDetails = customerDetails.companyDetails;
      details.companyDetails = {
        companyNumber: companyDetails?.companyNumber,
        companyName: companyDetails?.companyName
      };
    }

    return details;
  }

  getMeterDetailsForConsent() {
    const meterSelection = this.onboardForm.get('meterSelection')?.value;
    
    if (!meterSelection) {
      return undefined;
    }

    // Handle file upload case - we can't show individual meters from file
    if (meterSelection instanceof File) {
      return {
        meters: [{
          mpxn: `File: ${meterSelection.name}`,
          fuelType: 'Elec' as const,
          address: `File size: ${(meterSelection.size / 1024).toFixed(2)} KB`
        }]
      };
    }

    // Handle meter array case
    if (Array.isArray(meterSelection) && meterSelection.length > 0) {
      return {
        meters: meterSelection.map((meter: MeterDetails) => ({
          mpxn: meter.mpanCore,
          fuelType: 'Elec' as const,
          address: meter.addressAsLine || meter.addressId
        }))
      };
    }

    return undefined;
  }

  onSubmit() {
    if (!this.canFinish()) {
      return;
    }

    this.registrationInProgress = true;

    const formValue = this.onboardForm.getRawValue();
    const customerDetails = formValue.customerDetails;
    const personalDetails = customerDetails?.personalDetails;
    const meterSelection = formValue.meterSelection;

    let registrationDetails: RegistrationDetails = {
      firstname: personalDetails?.firstName,
      lastname: personalDetails?.lastName,
      email: personalDetails?.email,
      phone: personalDetails?.phone,
      signature: `${personalDetails?.firstName} ${personalDetails?.lastName}`,
      terms: true // Assuming terms are accepted through consent
    };

    if (customerDetails?.registeringAs === 'company') {
      const companyDetails = customerDetails.companyDetails;
      registrationDetails = {
        ...registrationDetails,
        orgId: companyDetails?.companyNumber,
        org: companyDetails?.companyName?.name || companyDetails?.companyName
      };
    }

    // Handle meter details
    if (meterSelection) {
      if (meterSelection instanceof File) {
        // Handle file upload case
        registrationDetails = {
          ...registrationDetails,
          mpanCount: 'multiple',
          multimeterFile: meterSelection
        };
      } else if (Array.isArray(meterSelection) && meterSelection.length > 0) {
        // Handle meter array case
        if (meterSelection.length === 1) {
          registrationDetails = {
            ...registrationDetails,
            mpan: meterSelection[0].mpanCore,
            meterType: this.isSmartMeter(meterSelection[0]) ? 'smart' : 'hh'
          };
        } else {
          registrationDetails = {
            ...registrationDetails,
            mpanCount: 'multiple'
            // Note: For multiple meters from individual searches, 
            // we might need to handle this differently based on API requirements
          };
        }
      }
    }

    console.log('Onboard Meter Details:', registrationDetails);

    this.regService.registerUser(registrationDetails).subscribe({
      next: (response) => {
        console.log('Meter onboard successful:', response);
        this.registrationInProgress = false;
        this.util.openSnackBar('Meter onboarded successfully!', 'Close', 'success');
        this.dialogRef.close(true);
      },
      error: (error) => {
        console.error('Error onboarding meter:', error);
        this.registrationInProgress = false;
        if (error?.error?.message?.[0]?.message) {
          this.util.openSnackBar(error.error.message[0].message, 'Close', 'error');
        } else {
          this.util.openSnackBar('Something went wrong. Please try again', 'Close', 'error');
        }
      }
    });
  }

  onCancel() {
    this.dialogRef.close(false);
  }
}
